/**
 * Settings Hook
 *
 * Custom hook to access system settings from frontend blocks.
 *
 * @package Enterprise\BookingSystem\Hooks
 */

/**
 * WordPress dependencies
 */
import { useEffect, useState } from "@wordpress/element";
import apiFetch from "@wordpress/api-fetch";

type PageUrls = {
	cartUrl: string;
	checkoutUrl: string;
	orderSuccessUrl: string;
	shopUrl: string;
};

type SettingsData = {
	[key: string]: unknown;
	cart_page_id: number;
	checkout_page_id: number;
	currency: string;
	order_success_page_id: number;
};

/**
 * Hook to get page URLs from configured settings.
 *
 * @returns Page URLs object
 */
export function usePageUrls(): PageUrls {
	const [urls, setUrls] = useState<PageUrls>({
		cartUrl: "/cart",
		checkoutUrl: "/checkout",
		orderSuccessUrl: "/order-success",
		shopUrl: "/shop",
	});

	useEffect(() => {
		const fetchSettings = async () => {
			try {
				const response = await apiFetch<{
					data: SettingsData;
					success: boolean;
				}>({
					path: "/ebs/v1/settings",
				});

			if (response.success) {
				const {
					cart_page_id,
					checkout_page_id,
					order_success_page_id,
				} = response.data;

					// Fetch page URLs from WordPress
					const pageRequests = [];

					if (cart_page_id) {
						pageRequests.push(
							apiFetch<{ link: string }>({
								path: `/wp/v2/pages/${cart_page_id}?_fields=link`,
							}).then((page) => ({
								type: "cart",
								url: page.link,
							})),
						);
					}

					if (checkout_page_id) {
						pageRequests.push(
							apiFetch<{ link: string }>({
								path: `/wp/v2/pages/${checkout_page_id}?_fields=link`,
							}).then((page) => ({
								type: "checkout",
								url: page.link,
							})),
						);
					}

					if (order_success_page_id) {
						pageRequests.push(
							apiFetch<{ link: string }>({
								path: `/wp/v2/pages/${order_success_page_id}?_fields=link`,
							}).then((page) => ({
								type: "success",
								url: page.link,
							})),
						);
					}

				const pages = await Promise.all(pageRequests);

				const urlOverrides: Partial<PageUrls> = {};

				for (const page of pages) {
					switch (page.type) {
						case "cart": {
							urlOverrides.cartUrl = page.url;

							break;
						}
						case "checkout": {
							urlOverrides.checkoutUrl = page.url;

							break;
						}
						case "success": {
							urlOverrides.orderSuccessUrl = page.url;

							break;
						}
						// No default
					}
				}

				setUrls((prev) => ({ ...prev, ...urlOverrides }));
				}
			} catch (error) {
				console.error("Failed to fetch settings:", error);
				// Keep default URLs on error
			}
		};

		void fetchSettings();
	}, []);

	return urls;
}

/**
 * Hook to get all settings.
 *
 * @returns Settings data
 */
export function useSettings() {
	const [settings, setSettings] = useState<null | SettingsData>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<null | string>(null);

	useEffect(() => {
		const fetchSettings = async () => {
			try {
				const response = await apiFetch<{
					data: SettingsData;
					success: boolean;
				}>({
					path: "/ebs/v1/settings",
				});

			if (response.success) {
				setSettings(response.data);
				} else {
					throw new Error("Failed to load settings");
				}
			} catch (error_) {
				setError(
					error_ instanceof Error ? error_.message : "Unknown error",
				);
			} finally {
				setIsLoading(false);
			}
		};

		void fetchSettings();
	}, []);

	return { settings, isLoading, error };
}
